/**
 * storeCore.js — pure storage transforms for the Bet Tracker.
 *
 * The tracker currently persists through an interim store (a
 * `trackedBets` array field on the user's userSettings/{uid} doc —
 * see betTrackerService.js for why). Everything in this module is
 * Firestore-free and framework-free so the read/write transforms can
 * be unit-tested in plain node, and so the eventual migration back
 * to the users/{uid}/bets subcollection only touches the adapter
 * section of betTrackerService.js, never these transforms.
 *
 * Records follow the shape documented in records.js. In the interim
 * store each array item additionally carries its `id` inline (the
 * subcollection design kept ids as doc ids, attached on read; here
 * there is no doc id, so the id travels with the record — the
 * service's public read shape is unchanged).
 */

/**
 * Recursively strip `undefined` values from plain data.
 *
 * Firestore rejects undefined field values anywhere in a write
 * ("Unsupported field value: undefined") and the web app's Firestore
 * instance is NOT configured with ignoreUndefinedProperties, so every
 * record/update is sanitized before it is written. null is a legal
 * Firestore value and is preserved; only undefined is removed.
 * Records contain plain data only (strings, numbers, booleans, null,
 * arrays, plain objects) — anything else is passed through untouched.
 */
export function stripUndefined(value) {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefined(item));
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value)) {
      const v = value[key];
      if (v === undefined) continue;
      out[key] = stripUndefined(v);
    }
    return out;
  }
  return value;
}

/** The bets array out of a settings-doc data object (or anything). */
export function betsFromDocData(data, field) {
  if (!data || !Array.isArray(data[field])) return [];
  return data[field];
}

/**
 * Newest-first ordering by createdAt (ISO strings sort
 * lexicographically), matching the old subcollection query's
 * orderBy('createdAt', 'desc'). Returns a new array.
 */
export function sortBetsNewestFirst(bets) {
  return [...(bets || [])].sort((a, b) => {
    const ka = a && a.createdAt ? String(a.createdAt) : '';
    const kb = b && b.createdAt ? String(b.createdAt) : '';
    if (ka === kb) return 0;
    return ka < kb ? 1 : -1;
  });
}

/** Append a record (already sanitized, id inline). New array. */
export function insertBet(bets, record) {
  return [...(bets || []), record];
}

/**
 * Merge `updates` into the bet with `betId`. Returns a new array, or
 * the SAME array reference when no bet matches (callers use that to
 * skip a pointless write).
 */
export function updateBetInList(bets, betId, updates) {
  const list = bets || [];
  let found = false;
  const next = list.map((bet) => {
    if (!bet || bet.id !== betId) return bet;
    found = true;
    return { ...bet, ...updates };
  });
  return found ? next : list;
}

/** Remove the bet with `betId`. New array (or same ref if absent). */
export function removeBetFromList(bets, betId) {
  const list = bets || [];
  const next = list.filter((bet) => bet && bet.id !== betId);
  return next.length === list.length ? list : next;
}
